/**
 * Универсальная размерная сетка обуви (Nike SB)
 * Содержит соответствие размеров EU, RU, US, UK, CM
 *
 * Источник значений: таблица как на рефе (Nike SB).
 */
export const SIZE_CHART = [
  { eu: 36.5, ru: 35.5, us: 4.5, uk: 4, cm: 23.5 },
  { eu: 37.5, ru: 36.5, us: 5, uk: 4.5, cm: 23.5 },
  { eu: 38, ru: 37, us: 5.5, uk: 5, cm: 24 },
  { eu: 38.5, ru: 37.5, us: 6, uk: 5.5, cm: 24 },
  { eu: 39, ru: 38, us: 6.5, uk: 6, cm: 24.5 },
  { eu: 40, ru: 39, us: 7, uk: 6, cm: 25 },
  { eu: 40.5, ru: 39.5, us: 7.5, uk: 6.5, cm: 25.5 },
  { eu: 41, ru: 40, us: 8, uk: 7, cm: 26 },
  { eu: 42, ru: 41, us: 8.5, uk: 7.5, cm: 26.5 },
  { eu: 42.5, ru: 41.5, us: 9, uk: 8, cm: 27 },
  { eu: 43, ru: 42, us: 9.5, uk: 8.5, cm: 27.5 },
  { eu: 44, ru: 43, us: 10, uk: 9, cm: 28 },
  { eu: 44.5, ru: 43.5, us: 10.5, uk: 9.5, cm: 28.5 },
  { eu: 45, ru: 44, us: 11, uk: 10, cm: 29 },
  { eu: 45.5, ru: 44.5, us: 11.5, uk: 10.5, cm: 29.5 },
  { eu: 46, ru: 45, us: 12, uk: 11, cm: 30 },
  { eu: 46.5, ru: 46, us: 12.5, uk: 11.5, cm: 30.5 },
  { eu: 47.5, ru: 46.5, us: 13, uk: 12, cm: 31 },
  { eu: 48, ru: 47, us: 13.5, uk: 12.5, cm: 31.5 },
]

function parseNumeric(value) {
  const normalized = String(value ?? "")
    .trim()
    .replace(",", ".")
    .replace(/\s+/g, "")
  const num = Number(normalized)
  return Number.isFinite(num) ? num : null
}

function normalizeStr(value) {
  return String(value ?? "")
    .trim()
    .toUpperCase()
}

/**
 * Получить минимальное и максимальное значение для типа размера
 * @param {string} sizeType - Тип размера (EU, RU, US, UK, CM)
 * @returns {{min: string|number, max: string|number}} Минимальное и максимальное значение
 */
export function getSizeLimits(sizeType) {
  const upperType = (sizeType || "EU").toUpperCase()
  const key = upperType === "CM" ? "cm" : upperType.toLowerCase()

  if (!SIZE_CHART.length) {
    return { min: null, max: null }
  }

  const values = SIZE_CHART.map((entry) => entry[key]).filter((v) => v != null)

  if (values.length === 0) {
    return { min: null, max: null }
  }

  // Для числовых типов (EU, RU, CM) находим min/max числовые значения
  if (upperType === "EU" || upperType === "RU" || upperType === "CM") {
    const numericValues = values
      .map((v) => parseNumeric(v))
      .filter((v) => v != null)
    if (numericValues.length === 0) {
      return { min: null, max: null }
    }
    return {
      min: Math.min(...numericValues),
      max: Math.max(...numericValues),
    }
  }

  // Для строковых типов (US, UK) возвращаем первый и последний элемент
  return {
    min: values[0],
    max: values[values.length - 1],
  }
}

/**
 * Проверить, является ли размер валидным для данного типа
 * @param {string|number} size - Значение размера
 * @param {string} sizeType - Тип размера (EU, RU, US, UK, CM)
 * @returns {boolean} true, если размер валиден
 */
export function isValidSize(size, sizeType) {
  const upperType = (sizeType || "EU").toUpperCase()
  const key = upperType === "CM" ? "cm" : upperType.toLowerCase()

  // Проверяем, существует ли такой размер в сетке
  return SIZE_CHART.some((entry) => {
    const chartValue = entry[key]
    if (chartValue == null) return false

    // Для числовых типов сравниваем как числа
    if (upperType === "EU" || upperType === "RU" || upperType === "CM") {
      const numSize = parseNumeric(size)
      const numChart = parseNumeric(chartValue)
      return numSize != null && numChart != null && numSize === numChart
    }

    // Для строковых типов:
    // - если оба значения приводятся к числу (US/UK с 0.5 и вводом "4,5") — сравниваем как числа
    // - иначе сравниваем как строки (без учета регистра)
    const numSize = parseNumeric(size)
    const numChart = parseNumeric(chartValue)
    if (numSize != null && numChart != null) {
      return numSize === numChart
    }
    return normalizeStr(size) === normalizeStr(chartValue)
  })
}

/**
 * Конвертировать размер из одного типа в другой
 * @param {string|number} size - Значение размера
 * @param {string} fromType - Исходный тип размера
 * @param {string} toType - Целевой тип размера
 * @returns {string|number|null} Конвертированное значение или null
 */
export function convertSize(size, fromType, toType) {
  const upperFrom = (fromType || "EU").toUpperCase()
  const upperTo = (toType || "EU").toUpperCase()
  const fromKey = upperFrom === "CM" ? "cm" : upperFrom.toLowerCase()
  const toKey = upperTo === "CM" ? "cm" : upperTo.toLowerCase()

  // Находим запись с соответствующим размером
  const entry = SIZE_CHART.find((row) => {
    const chartValue = row[fromKey]
    if (chartValue == null) return false

    // Для числовых типов сравниваем как числа
    if (upperFrom === "EU" || upperFrom === "RU" || upperFrom === "CM") {
      const numSize = parseNumeric(size)
      const numChart = parseNumeric(chartValue)
      return numSize != null && numChart != null && numSize === numChart
    }

    // Для строковых типов:
    // - если оба значения приводятся к числу — сравниваем как числа
    // - иначе сравниваем как строки (без учета регистра)
    const numSize = parseNumeric(size)
    const numChart = parseNumeric(chartValue)
    if (numSize != null && numChart != null) {
      return numSize === numChart
    }
    return normalizeStr(size) === normalizeStr(chartValue)
  })

  return entry ? entry[toKey] : null
}

/**
 * Найти ближайший валидный размер из размерной сетки
 * @param {string|number} size - Значение размера
 * @param {string} sizeType - Тип размера (EU, RU, US, UK, CM)
 * @returns {string|number|null} Ближайший валидный размер или null
 */
export function findClosestSize(size, sizeType) {
  const upperType = (sizeType || "EU").toUpperCase()
  const key = upperType === "CM" ? "cm" : upperType.toLowerCase()

  if (!SIZE_CHART.length) {
    return null
  }

  // Для числовых типов (EU, RU, CM) ищем ближайшее значение
  if (upperType === "EU" || upperType === "RU" || upperType === "CM") {
    const numSize = parseNumeric(size)
    if (numSize == null) return null

    // Сортируем размеры по близости к введенному значению
    const sortedSizes = SIZE_CHART.map((entry) => ({
      value: entry[key],
      diff: Math.abs((parseNumeric(entry[key]) ?? Number.MAX_VALUE) - numSize),
    })).filter((item) => item.value != null && parseNumeric(item.value) != null)

    if (sortedSizes.length === 0) return null

    // Находим ближайшее значение
    const closest = sortedSizes.reduce((prev, curr) =>
      curr.diff < prev.diff ? curr : prev
    )

    return closest.value
  }

  // Для строковых типов (US, UK) проверяем точное совпадение
  // Если нет точного совпадения, возвращаем первое значение из сетки как fallback
  const sizeStr = normalizeStr(size)
  const exactMatch = SIZE_CHART.find((entry) => {
    const chartValue = entry[key]
    if (chartValue == null) return false
    // Для US/UK поддерживаем ввод "4,5"
    const numSize = parseNumeric(size)
    const numChart = parseNumeric(chartValue)
    if (numSize != null && numChart != null) {
      return numSize === numChart
    }
    return normalizeStr(chartValue) === sizeStr
  })

  if (exactMatch) {
    return exactMatch[key]
  }

  // Если точного совпадения нет, возвращаем первое доступное значение
  const firstEntry = SIZE_CHART.find((entry) => entry[key] != null)
  return firstEntry ? firstEntry[key] : null
}
